import { useState } from 'react';
import { Popover } from '@base-ui/react/popover';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { ChatIntegrationProvider, IAppConnection, IChatIntegration } from '@interfaces/IChatIntegrations';
import { StatusDot } from '@components/ui/StatusDot/StatusDot';
import { ChevronRightIcon } from '@utils/icons';
import { useChatIntegrations } from '../../hooks/useChatIntegrations';
import { useConnectReturn } from '../../hooks/useConnectReturn';
import { ConnectAppsMenu } from './ConnectAppsMenu/ConnectAppsMenu';
import { PROVIDERS } from './providers';
import styles from './IntegrationsFooter.module.css';

const ICON_SIZE = 15;

type IConnected = IChatIntegration & { connection: IAppConnection };

interface ISummary {
  providers: ChatIntegrationProvider[];
  label: string;
  dot: string;
}

/** What the button says once something is connected. A broken connection comes first. */
function summarise(apps: IChatIntegration[], t: TFunction): ISummary | null {
  const connected = apps.filter((app): app is IConnected => app.connection !== null);
  const broken = connected.find((app) => app.connection.status === 'broken');
  if (broken) {
    return {
      providers: [broken.provider],
      label: t('comms.integrations.reconnectApp', { app: PROVIDERS[broken.provider].name }),
      dot: 'var(--color-error)',
    };
  }
  const [first, ...others] = connected;
  if (!first) return null;

  const anyOn = connected.some((app) => app.connection.enabled);
  const dot = anyOn ? 'var(--color-success-mid)' : 'var(--g300)';
  const providers = connected.map((app) => app.provider);
  if (others.length > 0) {
    return { providers, label: t('comms.integrations.appsConnected', { count: connected.length }), dot };
  }
  const app = PROVIDERS[first.provider].name;
  return { providers, label: t(anyOn ? 'comms.integrations.notesOn' : 'comms.integrations.notesOff', { app }), dot };
}

/**
 * The bottom of the chat sidebar: a button that opens the connect-apps
 * popover beside it, over the edge of the conversation. Once an app is
 * connected the button shows its state instead of every app's logo.
 */
export function IntegrationsFooter() {
  const { t } = useTranslation();
  const chatIntegrations = useChatIntegrations();
  const landed = useConnectReturn();

  // Back from connecting an app: the popover reopens on how it went. A failure
  // is said once: closing the popover clears it.
  const [open, setOpen] = useState(landed !== null);
  const [connectFailed, setConnectFailed] = useState(landed?.failed ? landed.provider : null);
  const onOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) setConnectFailed(null);
  };
  const summary = summarise(chatIntegrations.status.apps, t);
  const logos = summary?.providers ?? chatIntegrations.status.apps.map((app) => app.provider);

  return (
    <footer className={styles.root}>
      <Popover.Root open={open} onOpenChange={onOpenChange}>
        {/* Named by its state alone: the "Manage" beside it is a visual cue, not part of the name. */}
        <Popover.Trigger className={styles.trigger} aria-label={summary?.label ?? t('comms.integrations.connectApps')}>
          <span className={styles.icons}>
            {logos.map((provider) => {
              const { Logo } = PROVIDERS[provider];
              return (
                <span key={provider} className={styles.logo}>
                  <Logo size={ICON_SIZE} />
                </span>
              );
            })}
          </span>
          {summary ? (
            <>
              <span className={styles.label}>
                {summary.label}
                <StatusDot color={summary.dot} />
              </span>
              <span className={styles.manage}>{t('comms.integrations.manage')}</span>
            </>
          ) : (
            <>
              <span className={styles.label}>{t('comms.integrations.connectApps')}</span>
              <ChevronRightIcon size={14} />
            </>
          )}
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Positioner side="right" align="end" sideOffset={14} className={styles.positioner}>
            <Popover.Popup className={styles.popup}>
              <Popover.Arrow className={styles.arrow} />
              <ConnectAppsMenu {...chatIntegrations} failed={connectFailed} />
            </Popover.Popup>
          </Popover.Positioner>
        </Popover.Portal>
      </Popover.Root>
    </footer>
  );
}
